import { useEffect, useRef, useState } from 'react';

const BASE_URL = 'https://edge-config.vercel.com';

export type EdgeConfigOptions = {
    edgeConfigId: string;
    edgeConfigToken: string;
};

const getIsMaintenanceMode = async (options: EdgeConfigOptions) => {
    const response = await fetch(`${BASE_URL}/${options.edgeConfigId}/item/isMaintenanceMode`, {
        headers: {
            Authorization: `Bearer ${options.edgeConfigToken}`
        },
        method: 'GET'
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch: ${response.status}`);
    }

    return response.json() as Promise<boolean>;
};

interface Options {
    edgeConfigOptions: EdgeConfigOptions;
}

export const useIsMaintenanceMode = ({ edgeConfigOptions }: Options) => {
    const [current, setCurrent] = useState<boolean | null>(null);
    const previous = useRef<boolean | null>(null);

    const [isLoading, setIsLoading] = useState(true);

    const optionsRef = useRef(edgeConfigOptions);
    optionsRef.current = edgeConfigOptions;

    const timeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

    useEffect(() => {
        const callback = () =>
            getIsMaintenanceMode(optionsRef.current)
                .then((isMaintenanceMode) => {
                    setCurrent(isMaintenanceMode);
                    setIsLoading(false);

                    timeoutRef.current = setTimeout(() => {
                        callback();
                    }, 60000);
                })
                .catch(() => {});

        callback();

        return () => {
            clearTimeout(timeoutRef.current);
        };
    }, []);

    useEffect(() => {
        // Perform a hard reset when switching off the maintenance mode if there is any state
        if (previous.current === true && current === false) {
            window.location.reload();
        } else {
            previous.current = current;
        }
    }, [current]);

    return { data: current, isLoading };
};
